import PropTypes from 'prop-types';
import classNames from 'classnames';
import DateRangePicker from './date-range-picker';
import { Button, Popover2, Icon } from '@evertrue/et-components';
import { getMomentObjectInTimezone } from 'base/new-utils';

/**
 * DateRangePickerDropdown component. This is a button that opens a dropdown that contains a date range picker.
 *
 * @param {Object} props - The component props.
 * @param {function} [props.onDayClick=() => {}] - Callback function to handle day click events.
 * @param {Object} [props.range={ from: null, to: null }] - The selected date range.
 * @param {string} [props.triggerClassName] - Additional class name for the trigger button.
 * @param {string} [props.chevronClassName=''] - Additional class name for the chevron icon.
 * @param {string} [props.buttonType='secondary'] - The type of button to use.
 * @param {boolean} [props.allowClear] - Whether to allow clearing the selected date range.
 * @param {boolean} [props.disabled=false] - Whether the date picker is disabled.
 * @param {Array|Object} [props.disabledDays] - Days to be disabled in the date picker.
 * @param {boolean} [props.disableCloseOnDayClick] - Whether to disable closing the popover on day click.
 * @param {boolean} [props.disableWeekends=false] - Whether to disable selecting weekends.
 * @returns {JSX.Element} The DateRangePickerDropdown component.
 */
const DateRangePickerDropdown = ({
  onDayClick = () => {},
  range = { from: null, to: null },
  triggerClassName,
  chevronClassName = '',
  dropdownClassName = '',
  buttonType = 'secondary',
  allowClear,
  disabled = false,
  disabledDays,
  disableCloseOnDayClick,
  disableWeekends = false,
  onBlur = () => {},
  onClear = () => {},
  label = '',
}) => {
  const handleClick = (newRange) => {
    const normalizedRange = {
      from: newRange?.from ? new Date(new Date(newRange.from).setHours(0, 0, 0, 0)) : null,
      to: newRange?.to ? new Date(new Date(newRange.to).setHours(0, 0, 0, 0)) : null,
    };
    onDayClick(normalizedRange);
  };

  const displayDate = () => {
    if (range?.from && range?.to) {
      const fromDate = getMomentObjectInTimezone(range.from).format('MMM DD, YYYY');
      const toDate = getMomentObjectInTimezone(range.to).format('MMM DD, YYYY');
      return `${label} Date: ${fromDate} - ${toDate}`;
    } else if (range?.from) {
      return `From: ${getMomentObjectInTimezone(range.from).format('MMM DD, YYYY')}`;
    } else {
      return label ? `${label} Date` : 'Select Date Range';
    }
  };

  const weekendsDisabledDays = disableWeekends
    ? { daysOfWeek: [0, 6] } // 0 for Sunday, 6 for Saturday
    : {};

  const combinedDisabledDays = Array.isArray(disabledDays)
    ? [...disabledDays, weekendsDisabledDays]
    : [disabledDays, weekendsDisabledDays];

  return (
    <div>
      <Popover2
        fillcolor="#fff"
        trigger={({ openPopover }) => {
          return (
            <Button
              type={buttonType}
              className={classNames('date-range-picker-dropdown--btn', triggerClassName)}
              onClick={openPopover}
              disabled={disabled}
            >
              <span>
                <Icon icon="date-range" className="date-picker-dropdown--cal-icon" size={1} />
                {displayDate()}
              </span>
              <Icon icon="chevron-down" size={1} style={{ opacity: '65%' }} className={chevronClassName} />
            </Button>
          );
        }}
      >
        {({ closePopover }) => (
          <div className={`date-picker--dropdown ${dropdownClassName}`}>
            <DateRangePicker
              range={range}
              disabledDays={combinedDisabledDays}
              disableWeekends={disableWeekends}
              onDayClick={(newRange) => {
                if (!disableCloseOnDayClick) {
                  closePopover();
                }
                handleClick(newRange);
              }}
            />
            {allowClear && (
              <Button
                type="simple"
                onClick={() => {
                  closePopover();
                  onDayClick({ from: null, to: null });
                  onClear();
                }}
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </Popover2>
    </div>
  );
};

DateRangePickerDropdown.propTypes = {
  onDayClick: PropTypes.func.isRequired,
  range: PropTypes.shape({
    from: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    to: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  triggerClassName: PropTypes.string,
  disableCloseOnDayClick: PropTypes.bool,
  disabled: PropTypes.bool,
  chevronClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  allowClear: PropTypes.bool,
  buttonType: PropTypes.string,
  disabledDays: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  disableWeekends: PropTypes.bool,
  onBlur: PropTypes.func,
  onClear: PropTypes.func,
  label: PropTypes.string,
};

export default DateRangePickerDropdown;
